export const queryParams = {
        searchText:'',
        customerId:'',
        PageNumber:1,
        PageSize:10
}

export const DEFAULT_PAGE_SIZE = 10

// Compact Indian amount for SUMMARY tiles + TABLE columns only:
//   1,50,000     → ₹1.5L
//   10,00,000    → ₹10L
//   1,00,00,000  → ₹1Cr
//   below 1 lakh → full ₹ with commas (₹50,000)
// Detail views, forms, cheque print, amount-in-words and report exports keep
// the EXACT amount — do not use this there.
export const compactAmount = (amount) => {
  const n = Number(amount || 0)
  if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(2).replace(/\.?0+$/, '')}Cr`
  if (n >= 1_00_000) return `₹${(n / 1_00_000).toFixed(1).replace(/\.?0+$/, '')}L`
  return `₹${n.toLocaleString('en-IN')}`
}

// Current day/month keys in LOCAL time (IST for our users). Record dates come
// from <input type="date"> as timezone-naive strings, so comparing them against
// toISOString() (UTC) shifts "today" back a day between 00:00 and 05:30 IST.
export const localDayKey = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`

export const localMonthKey = (date = new Date()) => localDayKey(date).slice(0, 7)

// Normalise a server-provided ISO datetime (with or without a `Z` offset)
// into the IST-local YYYY-MM-DD / YYYY-MM day-or-month key. Using
// `String(iso).slice(0,10)` on an ISO string with `Z` returns the UTC calendar
// date — off-by-one for any IST time after 00:00 UTC. `new Date(iso)` parses
// respect for the offset, and `getDate/getMonth/getFullYear()` return
// local-time values.
export const isoToLocalDay = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return localDayKey(d)
}
export const isoToLocalMonth = (iso) => {
  const day = isoToLocalDay(iso)
  return day ? day.slice(0, 7) : ''
}

// Dropdown endpoints may return the list bare or wrapped in { data } / { result } envelopes.
export const toList = (res) => {
  const payload = res?.data ?? res
  const candidates = [payload, payload?.data, payload?.result, payload?.data?.data, payload?.result?.data]
  return candidates.find(Array.isArray) ?? []
}

// ── Cheque-book stock math ─────────────────────────────────────────────────
// A cheque *series* is the physical book of leaves running startNumber..endNumber.
// Leaves partition into Used + Cancelled + Blank = Total. Signed is NOT a fourth
// bucket — it's a subset of Used that counts every leaf ever signed, so it only
// ever grows and can never exceed Used.
// The three lifecycle states (Active / Low Stock / Exhausted) are derived purely
// from the entries recorded against a series — no separate persisted state.
// Kept as a single source of truth so ChequeInventory, ChequeSeries master,
// the Blank Stock report, and the Dashboard can't drift apart.
export const LOW_STOCK_RATIO = 0.1

export const seriesStatus = ({ total, blank }) => {
  if (total > 0 && blank === 0) return 'Exhausted'
  if (total > 0 && blank / total <= LOW_STOCK_RATIO) return 'Low Stock'
  return 'Active'
}

export const STATUS_FILTERS = ['All', 'Active', 'Low Stock', 'Exhausted']

// Aggregate a series list + entry list into one row per series carrying the
// five counters and the derived status. Both `status` and `stockStatus` are
// emitted so pre-existing consumers (blank-stock report used stockStatus) keep
// working byte-identical.
export const computeSeriesInventory = (series, entries) => {
  const entriesBySeries = new Map()
  ;(entries || []).forEach((entry) => {
    // Same defence-in-depth chain the app uses everywhere the entry->series
    // link is read (see ChequeInventory + reportRegistry blank-stock).
    const seriesId =
      entry.chequeSeriesId ?? entry.seriesId ?? entry.chequeSeries?.chequeSeriesId
    if (seriesId == null) return
    if (!entriesBySeries.has(seriesId)) entriesBySeries.set(seriesId, [])
    entriesBySeries.get(seriesId).push(entry)
  })

  return (series || []).map((s) => {
    const start = Number(s.startNumber) || 0
    const end = Number(s.endNumber) || 0
    const total = end >= start && start > 0 ? end - start + 1 : 0

    const seriesEntries = entriesBySeries.get(s.chequeSeriesId) ?? []
    const cancelled = seriesEntries.filter((e) => e.isCancelled).length
    // Signing is a one-way fact about a leaf, so this counts every signed entry
    // rather than only the not-yet-issued ones. The old `!e.isIssued` filter made
    // the counter *fall* each time a signed cheque was issued.
    // Read the `isSigned` flag, not the dates: SignedDate is a non-nullable
    // DateTime server-side, so unsigned rows serialise as "0001-01-01T00:00:00",
    // which is a non-empty — and therefore truthy — string in JS. The old
    // `signedDate || signedByName` test matched every row.
    const signed = seriesEntries.filter((e) => !e.isCancelled && e.isSigned).length
    // Leaves consumed off the book. Signed is a subset of this, not a sibling
    // bucket, so it must not be subtracted out. Used + cancelled + blank = total.
    const used = seriesEntries.length - cancelled
    const blank = Math.max(0, total - seriesEntries.length)

    const status = seriesStatus({ total, blank })

    return {
      // pass the original series through so callers that need its raw fields
      // (bankAccountId, customerBranchId, ...) don't have to look them up again
      ...s,
      chequeSeriesId: s.chequeSeriesId,
      seriesCode: s.seriesCode,
      seriesName: s.seriesName,
      bankName: s.bank?.bankName || '-',
      branchName: s.customerBranch?.companyName || '-',
      accountNumber: s.bankAccount?.accountNumber || '-',
      startNumber: s.startNumber,
      endNumber: s.endNumber,
      total,
      used,
      signed,
      blank,
      cancelled,
      status,
      // Alias kept for the blank-stock report's existing `stockStatus` field —
      // dropping this would silently break its facet + column exports.
      stockStatus: status,
    }
  })
}

// The backend's MarkDispatchDelivered updates the ChequeDispatch record only —
// it does NOT touch the ChequeEntry (isDispatched stays true, no isDelivered
// flag ships). To make Delivered status visible in list / report pages, we
// fetch dispatches, build a Set of delivered cheque IDs, and stamp
// `isDelivered: true` onto each matching cheque object before rendering.
// The chequeStatus() branch in ChequeDetailView/utils.js then reads that flag.
export const buildDeliveredIdSet = (dispatches) => {
  const set = new Set()
  ;(dispatches || []).forEach((d) => {
    if (d?.status === 'Delivered' && d?.chequeEntryId != null) {
      set.add(Number(d.chequeEntryId))
    }
  })
  return set
}

export const markDeliveredCheques = (cheques, deliveredIds) => {
  if (!deliveredIds || deliveredIds.size === 0) return cheques
  return (cheques || []).map((c) =>
    deliveredIds.has(Number(c?.chequeEntryId)) ? { ...c, isDelivered: true } : c
  )
}

// Cleared-by-bank is stored as a lifecycle event only (no ChequeEntry column),
// so list/report pages call apiGetClearedChequeIds() to know which cheques
// have cleared and stamp `isCleared: true` here. chequeStatus() then picks it
// up ahead of the Delivered / Dispatched branches.
export const buildClearedIdSet = (clearedIdsFromApi) => {
  const set = new Set()
  const raw = Array.isArray(clearedIdsFromApi) ? clearedIdsFromApi : []
  raw.forEach((v) => {
    if (v != null) set.add(Number(v))
  })
  return set
}

export const markClearedCheques = (cheques, clearedIds) => {
  if (!clearedIds || clearedIds.size === 0) return cheques
  return (cheques || []).map((c) =>
    clearedIds.has(Number(c?.chequeEntryId)) ? { ...c, isCleared: true } : c
  )
}

// The ChequeGuard endpoints are inconsistent: some return a bare array, some a paged
// envelope, and the envelope keys vary in casing. Normalise all of it in one place.
export const unwrapPaged = (response, fallbackPageSize = DEFAULT_PAGE_SIZE) => {
  const payload = response?.data ?? response

  if (Array.isArray(payload)) {
    return {
      rows: payload,
      paging: {
        currentPage: 1,
        pageSize: payload.length || fallbackPageSize,
        totalPages: 1,
        totalCount: payload.length,
        hasNext: false,
        hasPrevious: false,
      },
    }
  }

  const rows = [payload?.data, payload?.items, payload?.result].find(Array.isArray) ?? []
  const totalCount = payload?.totalCount ?? payload?.TotalCount ?? rows.length
  const pageSize = payload?.pageSize ?? payload?.PageSize ?? fallbackPageSize
  const currentPage = payload?.currentPage ?? payload?.pageIndex ?? payload?.PageNumber ?? 1

  return {
    rows,
    paging: {
      currentPage,
      pageSize,
      totalPages: payload?.totalPages ?? Math.max(1, Math.ceil(totalCount / (pageSize || 1))),
      totalCount,
      hasNext: payload?.hasNext ?? currentPage * pageSize < totalCount,
      hasPrevious: payload?.hasPrevious ?? currentPage > 1,
    },
  }
}